import { useLayoutEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Auth0Provider, useAuth0, type AppState } from '@auth0/auth0-react';
import { QueryProvider } from '@/providers/QueryProvider';
import { NavigationHistoryProvider } from '@/contexts/NavigationHistoryContext';
import { AppRoutes } from '@/routes/AppRoutes';
import { Toaster } from 'sonner';
import { setAuth0TokenGetter } from '@/services/auth/token-manager';
import { captureReturnTo } from '@/hooks/auth/useDeepLinkReturnTo';

/**
 * Bridge component that connects Auth0 token getter to the global token manager.
 * This allows the singleton API client to use Auth0 tokens for authentication.
 */
function Auth0TokenBridge({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // useLayoutEffect fires synchronously after DOM mutations but BEFORE any
  // useEffect hooks in child components (including React Query's initial fetch).
  // This guarantees auth0TokenGetter is set before API calls fire, fixing the
  // race condition where direct page navigation caused 403 "Not authenticated".
  useLayoutEffect(() => {
    if (isAuthenticated) {
      setAuth0TokenGetter(getAccessTokenSilently);
    } else {
      setAuth0TokenGetter(null);
    }

    return () => {
      setAuth0TokenGetter(null);
    };
  }, [isAuthenticated, getAccessTokenSilently]);

  return <>{children}</>;
}

/**
 * Auth0 onRedirectCallback — runs after Auth0 returns the user to
 * /auth/callback. If `appState.returnTo` is set (deep-link survival),
 * we mirror it into sessionStorage so `<CallbackPage>` can pop it after
 * provisioning. Mirroring rather than navigating directly because
 * CallbackPage still needs to call /auth/me to populate localStorage
 * before we send the user to their deep-linked destination.
 */
function handleAuth0Redirect(appState?: AppState): void {
  const returnTo = appState?.returnTo;
  if (typeof returnTo === 'string') {
    captureReturnTo(returnTo);
  }
}

function App() {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  return (
    <BrowserRouter>
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        onRedirectCallback={handleAuth0Redirect}
        useRefreshTokens={true}
        useRefreshTokensFallback={true}
        cacheLocation="localstorage"
        authorizationParams={{
          redirect_uri: `${window.location.origin}/auth/callback`,
          audience: audience,
          scope: 'openid profile email offline_access',
        }}
      >
        <Auth0TokenBridge>
          <QueryProvider>
            <NavigationHistoryProvider>
              <AppRoutes />
            </NavigationHistoryProvider>
            <Toaster position="top-right" richColors />
          </QueryProvider>
        </Auth0TokenBridge>
      </Auth0Provider>
    </BrowserRouter>
  );
}

export default App
